import './display.css';
import type { SeriesRef } from '../dicomweb';
import * as fusion from '../fusion/fusion';
import type { ViewerShell } from '../shell';
import * as lockTools from '../ui/locktools';
import * as multiScreen from '../ui/multiscreen';
import { displayKind, type DisplayKind } from './kind';
import { renderPdf } from './pdf';
import { renderSr } from './sr';
import { renderVideo } from './video';

/**
 * Display features beyond the image stack: render types pdf / video / sr (spec section 1.2), PET/CT
 * fusion, multi-screen, tool lock, and the lazy vtk.js views "MPR Obliquo (Beta)" and "3D".
 */

export { displayKind, type DisplayKind };

type Renderer = (shell: ViewerShell, series: SeriesRef, host: HTMLDivElement) => Promise<() => void>;

const RENDERERS: Record<Exclude<DisplayKind, 'stack'>, Renderer> = { pdf: renderPdf, video: renderVideo, sr: renderSr };
const SERIES_ERROR = ['Error al cargar la serie', 'por favor seleccione otra serie'] as const;

/** PDF, video and structured-report series are not cornerstone stacks. */
export function isSpecialSeries(series: SeriesRef): boolean {
  return displayKind(series) !== 'stack';
}

/** Shows a PDF / video / SR series in grid cell `index` (the shell routes these here from showSeriesIn). */
export async function showSpecialSeries(shell: ViewerShell, index: number, series: SeriesRef): Promise<void> {
  const viewport = shell.viewports()[index];
  const kind = displayKind(series);
  if (!viewport || kind === 'stack') return;
  const study = shell.studies().find((s) => s.studyInstanceUID === series.studyInstanceUID) ?? shell.study();
  viewport.setStudy(study);
  let shown: boolean;
  try {
    shown = await viewport.showCustom(series, kind, (host) => RENDERERS[kind](shell, series, host));
  } catch (error) {
    console.error('[viewer] series failed', series.seriesInstanceUID, error);
    viewport.showError(...SERIES_ERROR);
    return;
  }
  if (shown && shell.viewports()[index] === viewport) shell.emit('series-shown', { index, viewport, series });
}

// --- lazy vtk.js views ------------------------------------------------------------------------

type ObliqueModule = typeof import('../mpr/oblique');
type Volume3dModule = typeof import('../mpr/volume3d');
let obliqueModule: ObliqueModule | null = null;
let volume3dModule: Volume3dModule | null = null;

async function loadOblique(): Promise<ObliqueModule> {
  obliqueModule ??= await import('../mpr/oblique');
  return obliqueModule;
}

async function loadVolume3d(): Promise<Volume3dModule> {
  volume3dModule ??= await import('../mpr/volume3d');
  return volume3dModule;
}

export function isObliqueOpen(): boolean {
  return obliqueModule?.isObliqueOpen() ?? false;
}

export function isVolume3dOpen(): boolean {
  return volume3dModule?.isVolume3dOpen() ?? false;
}

export async function toggleOblique(shell: ViewerShell): Promise<void> {
  await (await loadOblique()).toggleOblique(shell);
}

export async function toggleVolume3d(shell: ViewerShell): Promise<void> {
  await (await loadVolume3d()).toggleVolume3d(shell);
}

// --- re-exports for commands / menus ------------------------------------------------------------

export const { fuseActive, fusionCandidates, isFused } = fusion;
export const { areToolsLocked, lockToolsLabel, toggleToolsLock, onToolsLockChange } = lockTools;
export const { openScreenDialog } = multiScreen;

interface ViewerDisplayDebug {
  kindOf(seriesUID: string): DisplayKind | undefined;
  fuse(cell: number, seriesUID: string): Promise<boolean>;
  unfuse(cell: number): void;
  fusion(cell: number): { colormap: string; opacity: number; layers: number; index: string | undefined } | undefined;
  setColormap(cell: number, id: string): void;
  setOpacity(cell: number, value: number): void;
  suvFactor(cell: number): number | null | undefined;
  openOblique(): Promise<void>;
  closeOblique(): void;
  isObliqueOpen(): boolean;
  rotateOblique(plane: 'axial' | 'coronal' | 'sagittal', degrees: number): Promise<boolean>;
  openVolume3d(): Promise<void>;
  closeVolume3d(): void;
  isVolume3dOpen(): boolean;
  cameraDirection(): Promise<number[] | null>;
  selectScreens(count: number): number;
  placeSeries(screen: number, cell: number, seriesUID: string): void;
  screen(): number;
  screenUrl(screen: number): string;
  toolsLocked(): boolean;
  setToolsLocked(locked: boolean): void;
}

declare global {
  interface Window {
    __viewerDisplay?: ViewerDisplayDebug;
  }
}

function debugApi(shell: ViewerShell): ViewerDisplayDebug {
  const cell = (index: number) => shell.viewports()[index];
  const find = (uid: string) => shell.studies().flatMap((s) => s.series).find((s) => s.seriesInstanceUID === uid);
  return {
    kindOf: (uid) => {
      const series = find(uid);
      return series ? displayKind(series) : undefined;
    },
    fuse: async (index, uid) => {
      const series = find(uid);
      const viewport = cell(index);
      return series && viewport ? fusion.fuse(shell, viewport, series) : false;
    },
    unfuse: (index) => {
      const viewport = cell(index);
      if (viewport) fusion.unfuse(viewport);
    },
    fusion: (index) => {
      const viewport = cell(index);
      if (!viewport || !fusion.isFused(viewport)) return undefined;
      const layers = (window.cornerstone as unknown as { getLayers(e: HTMLElement): Array<{ options: { opacity?: number }; viewport?: { colormap?: unknown } }> }).getLayers(viewport.element);
      const pet = layers[1];
      return { colormap: String(pet?.viewport?.colormap ?? ''), opacity: Number(pet?.options.opacity), layers: layers.length, index: viewport.container.dataset.fusionIndex };
    },
    setColormap: (index, id) => {
      const viewport = cell(index);
      if (viewport) fusion.setFusionColormap(viewport, id);
    },
    setOpacity: (index, value) => {
      const viewport = cell(index);
      if (viewport) fusion.setFusionOpacity(viewport, value);
    },
    suvFactor: (index) => {
      const viewport = cell(index);
      return viewport ? fusion.viewportSuvFactor(viewport) : undefined;
    },
    openOblique: async () => (await loadOblique()).openOblique(shell),
    closeOblique: () => obliqueModule?.closeOblique(shell),
    isObliqueOpen,
    rotateOblique: async (plane, degrees) => (await loadOblique()).rotateFrom(plane, degrees),
    openVolume3d: async () => (await loadVolume3d()).openVolume3d(shell),
    closeVolume3d: () => volume3dModule?.closeVolume3d(shell),
    isVolume3dOpen,
    cameraDirection: async () => (await loadVolume3d()).cameraDirection(),
    selectScreens: multiScreen.selectScreens,
    placeSeries: multiScreen.placeSeries,
    screen: multiScreen.currentScreen,
    screenUrl: (screen) => multiScreen.screenUrl(screen),
    toolsLocked: lockTools.areToolsLocked,
    setToolsLocked: lockTools.setToolsLocked,
  };
}

/** Installs fusion, tool lock and multi-screen; called by the shell before the first layout. */
export function installDisplayFeatures(shell: ViewerShell): void {
  fusion.install(shell);
  lockTools.install(shell);
  multiScreen.install(shell);
  window.__viewerDisplay = debugApi(shell);
}
